import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Typography,
  Switch,
  FormControlLabel,
  Chip,
  TextField,
  IconButton,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Checkbox,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import * as y from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import DonationUploadFile from './DonationUploadFile';
import { getS3Url, useGetVehicleLabels } from 'src/constants';
import { LoadingButton } from '@mui/lab';
import { Close, Edit, Pets } from '@mui/icons-material';
import useOrg from 'src/hooks/useOrg';
import useAuth from 'src/hooks/useAuth';
import useVehicles from 'src/hooks/useVehicles';
import useTeamMembers from 'src/hooks/useTeamMembers';
import {
  AddressInputWithoutMap,
  DateComponent,
  DayOfTheMonthSelector,
  processFoodTypes,
  TimeComponent,
  ToggleButtonComponent,
  useFoodTypesSelector,
  useGenerateFoodTypesObj,
} from 'src/hooks/useForm';
import moment from 'moment';
import { roundMinutes } from 'src/utils/time';
import useDaysOfTheWeek from 'src/hooks/useDaysOfTheWeek';
import {
  parseDonation,
  useAddDonationSchema,
  useGetDaysOfTheWeekValidator,
} from 'src/hooks/useCreateDonation';
import DonationFoodTypesSelector from './DonationFoodTypesSelector';
import useDriverAvailability from 'src/hooks/useDriverAvailability';
import Driver from './Driver';
import { Distance } from 'careit';
import { useMeasureFieldUnits } from './MeasureField';
import { useCheckAction } from 'src/utils/permissions';
import _isNumber from 'lodash/isNumber';
import getTimeZoneIdFromCoords from 'src/utils/getTimeZoneIdFromCoords';
import { v4 as uuidv4 } from 'uuid';
import { uploadImages } from 'src/components/form/UploadImages';
import {
  processRecurringIntervalData,
  recomputeEndDate,
} from 'src/utils/recurring';
import * as backend from 'src/backend';
import { useSnackbar } from 'notistack';
import {
  processFoodRunFormData,
  useAddFoodRunSchema,
} from 'src/views/schedule/FoodRun/helper';
import useEventBus from 'src/hooks/useEventBus';
import SocketIO from 'src/backend/SocketIO';
import useOrgsForFoodRunCreation from 'src/hooks/useOrgsForFoodRunCreation';
import useCheckCompletedOnboarding from 'src/hooks/useCheckCompletedOnboarding';
import { parseFoodRunForCopy } from './CreateFoodRun';

const RepeatEverySelector = ({
  control,
  data,
  setValue,
  errors,
  updateCorrectDates,
}) => {
  const daysOfTheWeek = useDaysOfTheWeek();
  const { t } = useTranslation('translation');

  return (
    <Stack spacing={2}>
      <Controller
        control={control}
        name="interval"
        render={({ field }) => (
          <FormControl fullWidth>
            <InputLabel>{t('donations.repeat-every')}</InputLabel>
            <Select
              {...field}
              onChange={(e) => {
                field.onChange(e.target.value);
                updateCorrectDates({ newInterval: e.target.value });
              }}
              label={t('donations.repeat-every')}
            >
              <MenuItem value="Daily">{t('donations.daily')}</MenuItem>
              <MenuItem value="Weekly">{t('donations.weekly')}</MenuItem>
              <MenuItem value="Bi-weekly">{t('donations.bi-weekly')}</MenuItem>
              <MenuItem value="Monthly">{t('donations.monthly')}</MenuItem>
            </Select>
          </FormControl>
        )}
      />
      {(data.interval === 'Weekly' || data.interval === 'Bi-weekly') && (
        <Box>
          <Typography variant="body2" color="text.secondary" mb={1}>
            {t('donations.repeat-on')}
          </Typography>
          <Stack direction="row" spacing={1}>
            {[...'SMTWTFS'].map((_, i) => (
              <ToggleButtonComponent
                key={i}
                label={daysOfTheWeek[i]}
                value={data.dayOfTheWeek.includes(i)}
                onChange={() => {
                  if (data.dayOfTheWeek.includes(i)) {
                    setValue(
                      'dayOfTheWeek',
                      data.dayOfTheWeek.filter((d) => d !== i)
                    );
                    setValue(`repeat-${i}`, false);
                  } else {
                    setValue('dayOfTheWeek', [...data.dayOfTheWeek, i]);
                    setValue(`repeat-${i}`, true);
                  }
                }}
                sx={{ margin: '5px', width: '100%' }}
              />
            ))}
          </Stack>
          {errors?.dayOfTheWeek?.message && (
            <Typography mt={1} variant="body2" color="error">
              {errors?.dayOfTheWeek?.message}
            </Typography>
          )}
        </Box>
      )}
      {data.interval === 'Monthly' && (
        <Box>
          <Controller
            control={control}
            name="dayOfTheMonth"
            render={({ field }) => (
              <DayOfTheMonthSelector
                {...field}
                onChange={(value) => {
                  field.onChange(value);
                }}
                error={errors?.dayOfTheMonth?.message}
              />
            )}
          />
        </Box>
      )}
    </Stack>
  );
};

const DatesSelectors = ({ control, data, errors }) => {
  const { t } = useTranslation('translation');
  return (
    <Stack mt={3} spacing={3}>
      <Stack spacing={1}>
        <Controller
          control={control}
          name="startDate"
          render={({ field }) => (
            <Box>
              <DateComponent
                {...field}
                sx={{
                  m: 0,
                  width: '100%',
                }}
                label={t('donations.start-date')}
                error={!!errors?.startDate?.message}
                helperText={errors?.startDate?.message}
              />
              {errors?.startDate?.message && (
                <Typography mt={1} variant="body2" color="error">
                  {errors?.startDate?.message}
                </Typography>
              )}
            </Box>
          )}
        />
        {data.occurrenceType !== 'indefinite' ? (
          <Controller
            control={control}
            name="endDate"
            render={({ field }) => (
              <Box>
                <DateComponent
                  {...field}
                  sx={{
                    width: '100%',
                  }}
                  minDate={data.startDate}
                  label={t('donations.end-date')}
                  error={!!errors?.endDate?.message}
                  helperText={errors?.endDate?.message}
                />
                {errors?.endDate?.message && (
                  <Typography mt={1} variant="body2" color="error">
                    {errors?.endDate?.message}
                  </Typography>
                )}
              </Box>
            )}
          />
        ) : null}
      </Stack>

      <Stack spacing={1}>
        <Controller
          control={control}
          name="startTime"
          render={({ field }) => (
            <Box>
              <TimeComponent
                {...field}
                sx={{
                  width: '100%',
                }}
                label={t('donations.start-time')}
                error={errors?.startTime?.message}
              />
              {errors?.startTime?.message && (
                <Typography mt={1} variant="body2" color="error">
                  {errors?.startTime?.message}
                </Typography>
              )}
            </Box>
          )}
        />
        <Controller
          control={control}
          name="endTime"
          render={({ field }) => (
            <Box>
              <TimeComponent
                {...field}
                sx={{
                  width: '100%',
                }}
                minTime={data.startTime}
                label={t('donations.end-time')}
                error={errors?.endTime?.message}
              />
              {errors?.endTime?.message && (
                <Typography mt={1} variant="body2" color="error">
                  {errors?.endTime?.message}
                </Typography>
              )}
            </Box>
          )}
        />
      </Stack>
    </Stack>
  );
};

const Image = ({ src, sx = {} }) => {
  return (
    <Box
      sx={{
        display: 'block',
        mx: 'auto !important',
        width: '250px',
        aspectRatio: '3/2',
        objectFit: 'contain',
        ...sx,
      }}
      component="img"
      src={src}
    />
  );
};

const CardSelector = ({ items }) => {
  return (
    <Stack
      direction={{
        xs: 'column',
        md: 'row',
      }}
      spacing={2}
    >
      {items.map(
        (
          {
            isSelected,
            imageSrc,
            name,
            onClick,
            imageWidth,
            fontSize = '16px',
            disabled = false,
          },
          idx
        ) => {
          return (
            <Stack
              key={idx}
              onClick={disabled ? undefined : onClick}
              width="100%"
              direction="column"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{
                border: '1px solid',
                borderColor: (t) =>
                  isSelected ? t.palette.secondary.main : t.palette.divider,
                boxShadow: (t) =>
                  isSelected ? `0 0 0 1px ${t.palette.secondary.main}` : 'none',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                borderRadius: '10px',
                p: 2,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
              }}
            >
              <Image
                sx={{
                  width: imageWidth || '200px',
                }}
                src={imageSrc}
              />
              <Typography
                fontWeight="bold"
                variant="body1"
                fontSize={fontSize}
                color="primary"
              >
                {name}
              </Typography>
            </Stack>
          );
        }
      )}
    </Stack>
  );
};

const EditSection = ({ label, stepToRedirect, setStep, children }) => {
  return (
    <Grid
      sx={{
        maxWidth: '100% !important',
      }}
      item
      xs={12}
      md={6}
    >
      <Stack
        sx={{
          width: '100%',
        }}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography color="gray" variant="body2">
          {label}
        </Typography>
        <IconButton
          onClick={() => {
            setStep(stepToRedirect);
          }}
        >
          <Edit
            sx={{
              color: 'gray',
              fontSize: '20px',
            }}
          />
        </IconButton>
      </Stack>
      <Stack>{children}</Stack>
    </Grid>
  );
};

const Container = ({ children, sx }) => {
  return (
    <Stack
      sx={{
        mx: 'auto',
        maxWidth: '650px',
        width: '100%',
        ...sx,
      }}
    >
      {children}
    </Stack>
  );
};

const processDonationData = ({
  donationTimeZoneId,
  destTimeZoneId,
  donationId,
  patternId,
  images,
  defaultDistanceUnit,
  user,
  ...data
}) => {
  const {
    occurrenceType,
    isRecurring,
    interval,
    daysOfTheWeek,
    startDate,
    endDate,
    startTime,
    endTime,
    isDST,
  } = processRecurringIntervalData({
    ...data,
    timezone_id: donationTimeZoneId,
  });

  return {
    isFoodRun: false,
    occurrenceType: occurrenceType === 'onetime' ? 'onetime' : 'recurring',
    isRecurring,
    interval,
    dayOfTheMonth: data.dayOfTheMonth,
    daysOfTheWeek,
    startDate,
    endDate: occurrenceType === 'indefinite' ? null : endDate,
    startTime,
    endTime,
    isDST,
    vehicleSize: data.vehicleSize,
    vehicle: data.deliveryOption === 0 ? data.vehicle : null,
    deliveryOption: data.deliveryOption,
    foodWeights: processFoodTypes(data.foodTypes),
    title: data.title,
    description: data.description,
    pickup_address: {
      ...data.pickupLocation,
      timeZoneId: donationTimeZoneId,
      place: data.pickupLocation?.name,
    },
    dest_address:
      data.receiverTeam && data.receiverTeam !== 'unassigned'
        ? {
            ...data.destLocation,
            timeZoneId: destTimeZoneId,
            place: data.destLocation?.name,
          }
        : {},
    images,
    receiverTeam:
      data.receiverTeam && data.receiverTeam !== 'unassigned'
        ? data.receiverTeam
        : null,
    driverId:
      data.deliveryOption === 0 && data.driverId !== 'unassigned'
        ? data.driverId
        : null,
    timezone_id: user.timezone_id,
    deliveryRadius:
      data.deliveryOption === 0 &&
      (!data?.receiverTeam || data.receiverTeam === 'unassigned')
        ? new Distance(
            parseFloat(data.deliveryRadius),
            defaultDistanceUnit.value
          ).as('mi')
        : null,
    donationId,
    patternId,
  };
};

const DialogCreateDonation = ({
  open,
  onClose: closeDialog,
  isFoodRun = false,
  invalidate,
  isRepost = false,
  isCopy = false,
  initialDonation,
}) => {
  const { t } = useTranslation('translation');

  const { user, isFoodMover, invalidateUserData } = useAuth();
  const [step, setStep] = useState(0);

  const foodTypes = useFoodTypesSelector();
  const vehicleTypes = useGetVehicleLabels();
  const daysOfTheWeek = useDaysOfTheWeek();
  const generateFoodTypesObj = useGenerateFoodTypesObj();
  const { defaultDistanceUnit } = useMeasureFieldUnits();

  const {
    donors,
    recipients,
    fetchNearByRecipients,
    invalidateMyOrgs,
    isMyRecipientsEmpty,
  } = useOrgsForFoodRunCreation({
    lat: user.lat,
    lng: user.lng,
    fetchOnMount: false,
  });
  const { getNearByNPOs, nearbyNPOs } = useOrg({
    history: null,
    user,
    isNonProfit: false,
    fetchOnMount: false,
  });
  const { enqueueSnackbar } = useSnackbar();
  const canCreateRecurring = useCheckAction('recurring-donation');

  const emitFetchFoodRuns = useEventBus('foodrun', 'fetch', null, null, {
    crossTabChannel: true,
  });
  const socket = SocketIO.getIO();

  const daysOfTheWeekValidator = useGetDaysOfTheWeekValidator();
  const checkCompletedOnboarding = useCheckCompletedOnboarding();

  const createAddDonationSchema = useAddDonationSchema();
  const createAddFoodRunSchema = useAddFoodRunSchema();

  const addFoodRunSchema = createAddFoodRunSchema(donors, recipients)
    .concat(
      y.object({
        driverId: y.string().notRequired(),
        receiverTeam: y
          .string()
          .notOneOf(['unassigned'], t('donations.select-a-receiver'))
          .test(
            'completed-onboarding',
            ({ value: receiver }) =>
              checkCompletedOnboarding.buildErrorMessage(nearbyNPOs, receiver),
            checkCompletedOnboarding.validate
          )
          .required(t('donations.select-a-receiver')),
      })
    )
    .test({
      test: daysOfTheWeekValidator,
    });

  const addDonationSchema = createAddDonationSchema(nearbyNPOs).concat(
    y.object({
      pickupLocation: y.object({}).when(['deliveryOption'], {
        is: (deliveryOption) => deliveryOption === 1,
        then: y.object({}).required(t('donations.donation-location-required')),
        otherwise: y.object({}).optional(),
      }),
    })
  );

  const schema = (isFoodRun ? addFoodRunSchema : addDonationSchema).shape({
    foodTypeNames: y
      .array()
      .of(y.string())
      .min(1, t('form.validation.required')),
  });

  const { vehicles = [], invalidate: fetchVehicles } = useVehicles({
    fetchOnMount: false,
  });
  const { activeMembers, invalidate: fetchTeamMembers } = useTeamMembers({
    fetchOnMount: false,
  });
  const { drivers, checkAvailabilityForDrivers } = useDriverAvailability();

  const [loading, setLoading] = useState(false);
  const [loadingImagesAI, setLoadingImagesAI] = useState(false);

  const useAIToFillDetailsFromLocalStorage = useMemo(() => {
    return (localStorage?.getItem('useAIToFillDetails') || 'true') === 'true';
  }, []);

  const defaultValues = {
    useAIToFillDetails: useAIToFillDetailsFromLocalStorage,
    images: [],
    foodTypes: {},
    foodTypeNames: [],
    filledFoodTypes: [],
    title: '',
    description: '',
    isPartialRescue: false,
    vehicleSize: 0,
    receiverTeam: 'unassigned',
    driverId: 'unassigned',
    vehicle: null,
    deliveryOption: 1,
    occurrenceType: 'onetime',
    startDate: new Date(
      moment().tz(user.timezone_id).format('YYYY-MM-DD HH:mm:ss')
    ),
    startTime: new Date(
      roundMinutes(moment()).tz(user.timezone_id).format('YYYY-MM-DD HH:mm:ss')
    ),
    endDate: new Date(
      moment().tz(user.timezone_id).format('YYYY-MM-DD HH:mm:ss')
    ),
    endTime: new Date(
      roundMinutes(moment())
        .tz(user.timezone_id)
        .add(5, 'hours')
        .format('YYYY-MM-DD HH:mm:ss')
    ),
    interval: 'Daily',
    dayOfTheWeek: [],
    dayOfTheMonth: {
      isLastDay: false,
      customDay: null,
      week: {
        number: null,
        dayOfTheWeek: null,
      },
    },
    deliveryRadius: user?.delivery_radius
      ? new Distance(user?.delivery_radius, 'mi').asRounded(
          defaultDistanceUnit.value
        )
      : null,
    pickupInstructions: '',
    dropOffInstructions: '',
    availableOnUnassign: false,
    destLocation: {},
    pickupLocation: !isFoodRun
      ? {
          lat: user.lat,
          lng: user.lng,
          formattedAddress: user.formatted_address,
          name: user.place,
          streetAddress: user.street_address,
          city: user.city,
          province: user.province,
          country: user.country,
          zipCode: user.zip_code,
          placeId: user.place_id,
          timeZoneId: user.timezone_id,
        }
      : {},
    isFoodMover,
    useCustomAddress: false,
  };

  const fieldsToValidateByStep = {
    0: ['images'],
    1: ['foodTypeNames'],
    2: ['title'],
    3: ['description'],
    4: ['isPartialRescue'],
    5: [
      'startDate',
      'endDate',
      'startTime',
      'endTime',
      'interval',
      'dayOfTheWeek',
      'dayOfTheMonth',
    ],
    6: ['foodTypes', 'driverId', 'receiverTeam', 'donor'],
    7: ['vehicleType'],
    8: ['pickupInstructions', 'dropOffInstructions'],
    9: [],
  };

  const {
    watch,
    formState: { errors },
    control,
    setValue,
    clearErrors,
    trigger,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const onClose = () => {
    closeDialog();
    setStep(0);
    reset();
  };
  const data = watch();

  const updateCorrectDates = ({
    newOccurrenceType = data.occurrenceType,
    newInterval = data.interval,
  }) => {
    if (newOccurrenceType === 'onetime') {
      setValue('endDate', data.startDate);
    } else {
      // Compute new end date based on repeat interval
      let newEndDate = (data.endDate = moment(data.startDate));
      if (newOccurrenceType !== 'onetime' && !newInterval) {
        setValue('interval', 'Daily');
      }
      if (newOccurrenceType !== 'indefinite' && newInterval) {
        recomputeEndDate(newInterval, newEndDate);
        setValue('endDate', new Date(newEndDate.format('YYYY-MM-DD HH:mm:ss')));
      }
    }
  };

  const onNextStep = async () => {
    if (!(await trigger(fieldsToValidateByStep[step]))) return;

    if (step === 9) {
      setLoading(true);
      // create donation
      let pickupTimeZoneId = null;
      if (_isNumber(data?.pickupLocation?.lat)) {
        pickupTimeZoneId = await getTimeZoneIdFromCoords(data?.pickupLocation);
      }
      let destTimeZoneId = null;
      if (_isNumber(data?.destLocation?.lat)) {
        destTimeZoneId = await getTimeZoneIdFromCoords(data?.destLocation);
      }

      //donationId will be used for image upload and the same donation id should be inserted as
      // id in donation table of one time donations
      const donationId = uuidv4();
      const patternId = uuidv4();

      const isRecurring = ['recurring', 'indefinite'].includes(
        data.occurrenceType
      );

      const s3Folder = isRecurring
        ? `recurring-pattern/${patternId}`
        : `donation/${donationId}`;

      const images = await uploadImages({
        images: data.images,
        folder: s3Folder,
        source: isFoodRun ? 'Create Food Run' : 'Create Donation',
      });
      if (isFoodRun) {
        const newFoodRun = processFoodRunFormData({
          ...data,
          pickup_address: {
            ...data.pickupLocation,
            timeZoneId: pickupTimeZoneId,
          },
          dest_address: {
            ...data.destLocation,
            timeZoneId: destTimeZoneId,
          },
          timezone_id: pickupTimeZoneId ?? user.timezone_id,
          receiver: data.receiverTeam,
          driver: data.driverId,
          donationId,
          patternId,
        });
        newFoodRun.images = images;

        backend.foodRun
          .create(newFoodRun)
          .then(() => {
            emitFetchFoodRuns();
            invalidateUserData();
            socket.emit('DONATION-UPDATE', {
              patternId,
              donationId: null,
              teamId: user.team_id,
              type: 'FOOD-RUN',
            });
          })
          .catch((err) => enqueueSnackbar(err.message, { variant: 'error' }));
      } else {
        const newDonation = processDonationData({
          donationId,
          patternId,
          defaultDistanceUnit,
          user,
          destTimeZoneId,
          donationTimeZoneId: pickupTimeZoneId,
          ...data,
          images,
        });

        const createNewDonation = () =>
          backend.donations
            .createRecurring(newDonation)
            .then((data) => {
              if (data.error && data.message) {
                return enqueueSnackbar(data.message, { variant: 'error' });
              }

              return invalidate?.();
            })
            .catch((err) => enqueueSnackbar(err.message, { variant: 'error' }));

        if (isRepost) {
          backend.donations
            .cancelDonation({
              donationId: initialDonation.id,
              teamId: user.team_id,
            })
            .then(() => createNewDonation())
            .then(history.goBack)
            .catch(() =>
              enqueueSnackbar(
                'There was an error while reposting your donation.',
                { variant: 'error' }
              )
            );
        } else {
          createNewDonation();
        }
      }
      setLoading(false);
      onClose();
      reset();
      setStep(0);
      return;
    }

    if (step === 0 && data.useAIToFillDetails) {
      setLoadingImagesAI(true);
      // fetch ai data
      // setValue('filledFoodTypes', ['dairy', 'meat', 'produce']);
      setLoadingImagesAI(false);
    }

    if (step === 7 && !isFoodRun) {
      setStep(9);
      return;
    }
    if (step === 3 && isFoodRun) {
      setStep(5);
      return;
    }
    setStep((prv) => prv + 1);
  };

  // Check driver availability when pickup time or team members change
  useEffect(() => {
    if (
      data.startDate &&
      data.startTime &&
      activeMembers.length &&
      user?.team_id
    ) {
      const userTimeZone = user.timezone_id || 'America/Los_Angeles';
      const date = moment(data.startDate);

      // If data.startTime is a Date, extract hours/minutes/seconds
      let hours = 0,
        minutes = 0,
        seconds = 0;
      if (data.startTime) {
        const t = new Date(data.startTime);
        hours = t.getHours();
        minutes = t.getMinutes();
        seconds = t.getSeconds();
      }

      // Combine date and time in the user's time zone
      const pickupMoment = moment.tz(
        {
          year: date.year(),
          month: date.month(),
          day: date.date(),
          hour: hours,
          minute: minutes,
          second: seconds,
        },
        userTimeZone
      );
      const pickupTimeISO = pickupMoment.toISOString();

      // Calculate end time based on data.endTime or default 2 hours after pickup
      let endMoment;
      if (data.endTime) {
        const tEnd = new Date(data.endTime);
        endMoment = moment.tz(
          {
            year: date.year(),
            month: date.month(),
            day: date.date(),
            hour: tEnd.getHours(),
            minute: tEnd.getMinutes(),
            second: tEnd.getSeconds(),
          },
          userTimeZone
        );
      }
      const endTimeISO = endMoment.toISOString();

      checkAvailabilityForDrivers({
        pickupTime: pickupTimeISO,
        endTime: endTimeISO,
        occurrenceType: data.occurrenceType,
        interval: data.interval,
        daysOfTheWeek: data.dayOfTheWeek,
        dayOfTheMonth: data.dayOfTheMonth,
      });
    }
  }, [
    data.startDate,
    data.startTime,
    data.endDate,
    data.endTime,
    data.occurrenceType,
    data.interval,
    data.dayOfTheWeek,
    data.dayOfTheMonth,
    activeMembers,
    user?.team_id,
  ]);

  useEffect(() => {
    if (data.receiverTeam && data.receiverTeam !== 'unassigned') {
      let selectedTeam;
      if (isFoodRun) {
        selectedTeam = recipients.find(
          ({ team_id }) => team_id === data.receiverTeam
        );
      } else {
        selectedTeam =
          nearbyNPOs.find(({ team_id }) => team_id === data.receiverTeam) ?? {};
      }
      if (!selectedTeam) return;
      setValue('destLocation', {
        lat: selectedTeam.lat,
        lng: selectedTeam.lng,
        formattedAddress: selectedTeam.formatted_address,
        name: selectedTeam.place,
        streetAddress: selectedTeam.street_address,
        city: selectedTeam.city,
        province: selectedTeam.province,
        country: selectedTeam.country,
        zipCode: selectedTeam.zip_code,
        placeId: selectedTeam.place_id,
        timeZoneId: selectedTeam.timezone_id,
      });
    }
  }, [data.receiverTeam, nearbyNPOs, recipients, isFoodRun]);

  useEffect(() => {
    if (data.donor && isFoodRun) {
      const selectedDonor = donors.find((team) => team.team_id === data.donor);
      if (isMyRecipientsEmpty && selectedDonor) {
        fetchNearByRecipients({
          donorLat: selectedDonor.lat,
          donorLng: selectedDonor.lng,
          donorTeamId: selectedDonor.team_id,
        });
      } else if (selectedDonor) {
        invalidateMyOrgs(selectedDonor.team_id);
      }
      if (_isNumber(selectedDonor?.lat)) {
        setValue('pickupLocation', {
          lat: selectedDonor.lat,
          lng: selectedDonor.lng,
          formattedAddress: selectedDonor.formatted_address,
          name: selectedDonor.place,
          streetAddress: selectedDonor.street_address,
          city: selectedDonor.city,
          province: selectedDonor.province,
          country: selectedDonor.country,
          zipCode: selectedDonor.zip_code,
          placeId: selectedDonor.place_id,
          timeZoneId: selectedDonor.timezone_id,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.donor, isFoodRun]);

  useEffect(() => {
    if (open) {
      fetchVehicles();
      fetchTeamMembers();
      if (isFoodRun) {
        invalidateMyOrgs();
      } else {
        getNearByNPOs({
          lat: user.lat,
          lng: user.lng,
          includeSelf: false,
          isDonationCreation: true,
        });
      }
    }
  }, [open, user.lat, user.lng, isFoodRun]);

  useEffect(() => {
    if (open && initialDonation) {
      if (isFoodRun) {
        const initialData = parseFoodRunForCopy(
          initialDonation,
          user,
          vehicleTypes
        );

        reset({
          ...defaultValues,
          ...initialData,
          foodTypeNames: Object.keys(initialData.foodTypes),
          receiverTeam: initialData.receiver,
          pickupLocation: initialData.pickup_address,
          destLocation: initialData.dest_address,
        });
      } else {
        const initialData = parseDonation(
          initialDonation,
          user,
          isRepost,
          isCopy
        );
        reset({
          ...defaultValues,
          ...initialData,
          foodTypeNames: Object.keys(initialData.foodTypes),
          pickupLocation: initialData.location,
          vehicleSize: {
            CAR: 0,
            VAN: 1,
            TRUCK: 2,
          }[initialDonation.vehicle],
        });
      }
    }
  }, [open, JSON.stringify(initialDonation)]);

  useEffect(() => {
    if (typeof data.useAIToFillDetails === 'boolean') {
      localStorage.setItem('useAIToFillDetails', data.useAIToFillDetails);
    }
  }, [data.useAIToFillDetails]);

  return (
    <Dialog maxWidth="md" fullWidth open={open} onClose={() => {}}>
      <Stack pt={2} pr={2} direction="row" justifyContent="end">
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </Stack>
      <Box
        component="form"
        sx={{
          mb: 3,
          width: '100%',
          mx: 'auto',
        }}
      >
        <DialogContent
          sx={{
            pt: 0,
            pb: 1,
          }}
        >
          {step === 0 ? (
            <Container key="step-1">
              <Box textAlign="center">
                <Image src="/static/illustrations/create-donation/step1.svg" />
                <Typography fontWeight="bold" variant="h3">
                  {t('donations.its-a-miracle')}
                </Typography>

                <Typography mt={2} variant="body2" color="gray">
                  {t('donations.step-1-description-1')}
                </Typography>
                <Typography mt={2} variant="body2" color="gray">
                  {t('donations.step-1-description-2')}
                </Typography>
              </Box>

              <Controller
                control={control}
                name="images"
                render={({ field }) => {
                  return (
                    <DonationUploadFile
                      sx={{
                        mt: 3,
                      }}
                      error={errors?.images?.message}
                      value={field.value?.map((image) => {
                        const isUrl = typeof image === 'string';
                        if (isUrl) {
                          return image;
                        }
                        return image.file;
                      })}
                      onChange={(files) => {
                        field.onChange(
                          files.map((image) => {
                            const isUrl = typeof image === 'string';
                            if (isUrl) {
                              return image;
                            }
                            return { file: image };
                          })
                        );
                      }}
                    />
                  );
                }}
              />
              <Controller
                control={control}
                name="useAIToFillDetails"
                render={({ field }) => {
                  return (
                    <FormControlLabel
                      sx={{
                        mt: 1,
                      }}
                      control={
                        <Switch
                          color="secondary"
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label={t('donations.use-ai-to-fill-details')}
                    />
                  );
                }}
              />
            </Container>
          ) : step === 1 ? (
            <Container key="step-2">
              <Box textAlign="center">
                <Image src="/static/illustrations/create-donation/step2.svg" />
                <Typography fontWeight="bold" variant="h3">
                  {t('donations.looking-delicious')}
                </Typography>
                {data.useAIToFillDetails && data.filledFoodTypes.length > 0 && (
                  <Typography mt={2} variant="body2" color="gray">
                    {t('donations.step-2-description-1')}{' '}
                    <Typography
                      variant="body2"
                      component="span"
                      color="secondary"
                    >
                      {foodTypes
                        .filter((foodType) =>
                          data.foodTypeNames.includes(foodType.key)
                        )
                        .map((foodType) => foodType.title.toLowerCase())
                        .join(', ')}
                      .
                    </Typography>
                  </Typography>
                )}
                <Typography mt={2} variant="body2" color="gray">
                  {t('donations.step-2-description-2')}
                </Typography>
              </Box>
              <Grid container spacing={1.5} mt={3}>
                {foodTypes.map((foodType, idx) => {
                  return (
                    <Grid item key={idx} xs={12} sm={4}>
                      <Chip
                        sx={{
                          width: '100%',
                        }}
                        square
                        style={{
                          textTransform: 'capitalize',
                        }}
                        icon={
                          <foodType.icon
                            sx={{
                              width: 20,
                              height: 20,
                              marginRight: '3px !important',
                            }}
                          />
                        }
                        label={foodType.title}
                        color={
                          data.foodTypeNames.includes(foodType.id)
                            ? 'primary'
                            : 'default'
                        }
                        variant={
                          data.foodTypeNames.includes(foodType.id)
                            ? 'contained'
                            : 'outlined'
                        }
                        onClick={() => {
                          let newFoodTypeNames = [...data.foodTypeNames];
                          if (data.foodTypeNames.includes(foodType.id)) {
                            newFoodTypeNames = newFoodTypeNames.filter(
                              (type) => type !== foodType.id
                            );
                            setValue('foodTypeNames', newFoodTypeNames);
                          } else {
                            newFoodTypeNames = [
                              ...newFoodTypeNames,
                              foodType.id,
                            ];
                            setValue('foodTypeNames', newFoodTypeNames);
                          }

                          setValue(
                            'foodTypes',
                            generateFoodTypesObj({
                              foodTypeNames: newFoodTypeNames,
                              isPartialRescue: data.isPartialRescue,
                              existingFoodTypes: data.foodTypes,
                            })
                          );
                        }}
                      />
                    </Grid>
                  );
                })}
              </Grid>
              {errors?.foodTypeNames?.message && (
                <Typography mt={2} variant="body2" color="error">
                  {errors?.foodTypeNames?.message}
                </Typography>
              )}
            </Container>
          ) : step === 2 ? (
            <Container key="step-3">
              <Box textAlign="center">
                <Image src="/static/illustrations/create-donation/step3.svg" />
                <Typography fontWeight="bold" variant="h3">
                  {t('donations.lets-begin-with-a-title')}
                </Typography>
                <Typography mt={2} variant="body2" color="gray">
                  {isFoodRun
                    ? t('donations.step-3-description', {
                        context: 'food-run',
                      })
                    : t('donations.step-3-description', {
                        context: 'donation',
                      })}
                </Typography>
              </Box>
              <Stack direction="column" spacing={1} mt={2}>
                <Controller
                  control={control}
                  name="title"
                  render={({ field }) => (
                    <TextField
                      size="medium"
                      label={t('donations.title')}
                      error={errors?.title?.message}
                      helperText={errors?.title?.message}
                      {...field}
                      fullWidth
                    />
                  )}
                />
              </Stack>
            </Container>
          ) : step === 3 ? (
            <Container key="step-4">
              <Box textAlign="center">
                <Image src="/static/illustrations/create-donation/step4.svg" />
                <Typography fontWeight="bold" variant="h3">
                  {t('donations.describe-your')}{' '}
                  {isFoodRun
                    ? t('donations.food-run')
                    : t('donations.donation')}
                </Typography>
                <Typography mt={2} variant="body2" color="gray">
                  {t('donations.step-4-description')}
                </Typography>
              </Box>
              <Stack direction="column" spacing={1} mt={2}>
                <Controller
                  control={control}
                  name="description"
                  render={({ field }) => {
                    return (
                      <TextField
                        size="medium"
                        label={t('donations.description')}
                        error={errors?.description?.message}
                        helperText={errors?.description?.message}
                        {...field}
                        fullWidth
                        multiline
                        rows={4}
                      />
                    );
                  }}
                />
              </Stack>
            </Container>
          ) : step === 4 ? (
            <Container key="step-5">
              <Box textAlign="center">
                <Image src="/static/illustrations/create-donation/step5.svg" />
                <Typography fontWeight="bold" variant="h3">
                  {t('donations.is-this-donation-suitable-for-partial-rescue')}
                </Typography>
                <Typography mt={2} variant="body2" color="gray">
                  {t('donations.step-5-description')}
                </Typography>
              </Box>
              <Stack mt={2}>
                <CardSelector
                  items={[
                    {
                      name: t('donations.partial-rescue-allowed'),
                      imageSrc:
                        '/static/illustrations/create-donation/cloud.svg',
                      isSelected: data.isPartialRescue,
                      onClick: () => {
                        setValue(
                          'foodTypes',
                          generateFoodTypesObj({
                            foodTypeNames: data.foodTypeNames,
                            isPartialRescue: true,
                            existingFoodTypes: data.foodTypes,
                          })
                        );
                        setValue('isPartialRescue', true);
                      },
                    },
                    {
                      name: t('donations.must-be-picked-all-at-once'),
                      imageSrc:
                        '/static/illustrations/create-donation/cloud.svg',
                      isSelected: !data.isPartialRescue,
                      onClick: () => {
                        setValue(
                          'foodTypes',
                          generateFoodTypesObj({
                            foodTypeNames: data.foodTypeNames,
                            isPartialRescue: false,
                            existingFoodTypes: data.foodTypes,
                          })
                        );
                        setValue('isPartialRescue', false);
                      },
                    },
                  ]}
                />
              </Stack>
            </Container>
          ) : step === 5 ? (
            <Container key="step-6">
              <Box textAlign="center">
                <Typography fontWeight="bold" variant="h3">
                  <Image src="/static/illustrations/create-donation/step8.svg" />
                  {t('donations.please-set-the-frequency-and-enter-the-dates')}
                </Typography>
                <Typography mt={2} variant="body2" color="gray">
                  {isFoodRun
                    ? t('donations.step-8-description', {
                        context: 'food-run',
                      })
                    : t('donations.step-8-description', {
                        context: 'donation',
                      })}
                </Typography>
              </Box>

              <Stack mt={3}>
                <CardSelector
                  items={[
                    {
                      name: t('donations.one-time'),
                      imageSrc: `/static/illustrations/create-donation/one-time.svg`,
                      isSelected: data.occurrenceType === 'onetime',
                      imageWidth: '120px',
                      fontSize: '14px',
                      onClick: () => {
                        setValue('occurrenceType', 'onetime');
                        updateCorrectDates({ newOccurrenceType: 'onetime' });
                      },
                    },
                    {
                      name: canCreateRecurring
                        ? t('donations.recurring')
                        : t('donations.recurring', { context: 'plan' }),
                      imageSrc: `/static/illustrations/create-donation/recurring.svg`,
                      isSelected: data.occurrenceType === 'recurring',
                      imageWidth: '120px',
                      fontSize: '14px',
                      onClick: () => {
                        setValue('occurrenceType', 'recurring');
                        updateCorrectDates({ newOccurrenceType: 'recurring' });
                      },
                      disabled: !canCreateRecurring,
                    },
                    {
                      name: canCreateRecurring
                        ? t('donations.indefinite')
                        : t('donations.indefinite', { context: 'plan' }),
                      imageSrc: `/static/illustrations/create-donation/indefinite.svg`,
                      isSelected: data.occurrenceType === 'indefinite',
                      imageWidth: '120px',
                      fontSize: '14px',
                      onClick: () => {
                        setValue('occurrenceType', 'indefinite');
                        updateCorrectDates({ newOccurrenceType: 'indefinite' });
                      },
                      disabled: !canCreateRecurring,
                    },
                  ]}
                />

                <Stack mt={3}>
                  {data.occurrenceType === 'onetime' ? (
                    <DatesSelectors
                      control={control}
                      data={data}
                      errors={errors}
                    />
                  ) : (
                    <Stack>
                      <RepeatEverySelector
                        control={control}
                        setValue={setValue}
                        data={data}
                        errors={errors}
                        updateCorrectDates={updateCorrectDates}
                      />
                      <DatesSelectors
                        control={control}
                        data={data}
                        errors={errors}
                      />
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </Container>
          ) : step === 6 ? (
            <Stack key="step-7">
              <Container>
                <Box textAlign="center">
                  <Typography fontWeight="bold" variant="h3">
                    {t('donations.how-much-of-each-donation-type-is-available')}
                  </Typography>
                  <Typography mt={2} variant="body2" color="gray">
                    {t('donations.step-6-description')}
                  </Typography>
                </Box>
              </Container>

              <Container
                sx={{
                  mt: 3,
                  maxWidth: '750px',
                }}
              >
                <Stack
                  sx={{
                    mb: 2,
                  }}
                >
                  {!isFoodRun && (
                    <Controller
                      control={control}
                      name="deliveryOption"
                      render={({ field }) => (
                        <FormControl>
                          <InputLabel>
                            {t('donations.delivery-option')}
                          </InputLabel>
                          <Select
                            label={t('donations.delivery-option')}
                            {...field}
                          >
                            <MenuItem value={0}>
                              {t('donations.self-deliver')}
                            </MenuItem>
                            <MenuItem value={1}>
                              {t('donations.reciever-picks-up')}
                            </MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  )}
                  {isFoodRun && (
                    <Controller
                      control={control}
                      name="donor"
                      render={({ field }) => (
                        <FormControl error={errors?.donor?.message}>
                          <InputLabel>{t('donations.donor')}</InputLabel>
                          <Select label={t('donations.donor')} {...field}>
                            {[...donors.filter((d) => d.team_id)].map(
                              (donor) => (
                                <MenuItem
                                  key={donor.team_id}
                                  value={donor.team_id}
                                >
                                  {donor.name} &nbsp;
                                  <Typography
                                    color="text.secondary"
                                    variant="overline"
                                  >
                                    {donor.formatted_address}
                                  </Typography>
                                </MenuItem>
                              )
                            )}
                          </Select>
                          {errors?.donor?.message && (
                            <FormHelperText error>
                              {errors?.donor?.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  )}
                  {((isFoodRun && data.donor) ||
                    (!isFoodRun && !data.isPartialRescue)) && (
                    <Controller
                      control={control}
                      name="receiverTeam"
                      render={({ field }) => (
                        <FormControl
                          sx={{
                            mt: 2,
                          }}
                          error={errors?.receiverTeam?.message}
                        >
                          <InputLabel>{t('donations.receiver')}</InputLabel>
                          <Select label={t('donations.receiver')} {...field}>
                            <MenuItem value="unassigned">
                              {t('donations.unassigned')}
                            </MenuItem>
                            {(isFoodRun ? recipients : nearbyNPOs).map(
                              (recipient) => (
                                <MenuItem
                                  key={recipient.id}
                                  value={recipient.team_id}
                                >
                                  <>
                                    {recipient.animal_food_rescuer_type ? (
                                      <>
                                        <Pets fontSize="small" sx={{ mr: 1 }} />
                                      </>
                                    ) : null}
                                    {recipient.name} &nbsp;
                                    <Typography
                                      color="text.secondary"
                                      variant="overline"
                                    >
                                      {recipient.formatted_address}
                                    </Typography>
                                  </>
                                </MenuItem>
                              )
                            )}
                          </Select>
                          {errors?.receiverTeam?.message && (
                            <FormHelperText error>
                              {errors?.receiverTeam?.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  )}
                  {!isFoodRun &&
                  data.deliveryOption === 0 &&
                  (!data.receiverTeam || data.receiverTeam === 'unassigned') ? (
                    <Controller
                      control={control}
                      name="deliveryRadius"
                      render={({ field }) => (
                        <TextField
                          sx={{
                            mt: 2,
                          }}
                          {...field}
                          label={t('donations.delivery-radius', {
                            unit: defaultDistanceUnit.label,
                          })}
                        />
                      )}
                    />
                  ) : null}
                  {(isFoodRun ||
                    (data.deliveryOption === 0 &&
                      (data.isPartialRescue ||
                        data.receiverTeam !== 'unassigned'))) && (
                    <Stack mt={2} direction="row" spacing={2}>
                      <Controller
                        control={control}
                        name="driverId"
                        render={({ field }) => (
                          <FormControl
                            error={errors?.driverId?.message}
                            fullWidth
                          >
                            <InputLabel>{t('donations.driver')}</InputLabel>
                            <Select label={t('donations.driver')} {...field}>
                              <MenuItem value="unassigned">
                                {t('donations.unassigned')}
                              </MenuItem>
                              {drivers.map((driver) => (
                                <MenuItem key={driver.id} value={driver.id}>
                                  <Driver user={driver} />
                                </MenuItem>
                              ))}
                            </Select>
                            {errors?.driverId?.message && (
                              <FormHelperText error>
                                {errors?.driverId?.message}
                              </FormHelperText>
                            )}
                          </FormControl>
                        )}
                      />

                      <Controller
                        control={control}
                        name="vehicle"
                        render={({ field }) => (
                          <FormControl fullWidth>
                            <InputLabel>{t('donations.vehicle')}</InputLabel>
                            <Select label={t('donations.vehicle')} {...field}>
                              {vehicles.map((vehicle) => (
                                <MenuItem key={vehicle.id} value={vehicle.id}>
                                  {vehicle.name} ({vehicle.capacity.value}{' '}
                                  {vehicle.capacity.unit})
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      />
                    </Stack>
                  )}

                  {isFoodRun && data.donor && (
                    <Controller
                      control={control}
                      name="useCustomAddress"
                      render={({ field }) => (
                        <FormControlLabel
                          sx={{
                            mt: 2,
                          }}
                          control={
                            <Checkbox
                              checked={field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                            />
                          }
                          label={t('donations.use-custom-address')}
                        />
                      )}
                    />
                  )}
                  {(data.useCustomAddress || !isFoodRun) && (
                    <Box mt={2}>
                      <Controller
                        control={control}
                        name="pickupLocation"
                        render={({ field }) => (
                          <AddressInputWithoutMap
                            {...field}
                            label={
                              isFoodRun
                                ? t('donations.custom-pickup-address')
                                : t('donations.donation-location')
                            }
                            onChange={(value) => {
                              field.onChange(value);
                            }}
                          />
                        )}
                      />
                    </Box>
                  )}

                  {(data.useCustomAddress ||
                    (data.deliveryOption === 0 &&
                      data.receiverTeam !== 'unassigned' &&
                      !isFoodRun)) && (
                    <Box mt={2}>
                      <Controller
                        control={control}
                        name="destLocation"
                        render={({ field }) => (
                          <AddressInputWithoutMap
                            {...field}
                            label={
                              isFoodRun
                                ? t('donations.custom-destination-address')
                                : t('donations.destination-location')
                            }
                            onChange={(value) => {
                              field.onChange(value);
                            }}
                          />
                        )}
                      />
                    </Box>
                  )}
                </Stack>
                <DonationFoodTypesSelector
                  includeFoodTypes={false}
                  value={data.foodTypes}
                  onChange={(value) => {
                    const keys = Object.keys(value);
                    setValue(
                      'foodTypeNames',
                      keys.map((key) => key.replaceAll('-', '_'))
                    );

                    setValue('foodTypes', value);
                  }}
                  recipients={isFoodRun ? recipients : nearbyNPOs}
                  isPartialRescue={data.isPartialRescue}
                  errors={errors}
                />
              </Container>
            </Stack>
          ) : step === 7 ? (
            <Container key="step-7">
              <Box textAlign="center">
                <Image src="/static/illustrations/create-donation/step7.svg" />
                <Typography fontWeight="bold" variant="h3">
                  {t('donations.what-type-of-vehicle-is-needed')}
                </Typography>
                <Typography mt={2} variant="body2" color="gray">
                  {t('donations.step-7-description')}
                </Typography>
              </Box>

              <Stack mt={3}>
                <CardSelector
                  items={vehicleTypes.map((v) => ({
                    name: v.label,
                    imageSrc: `/static/illustrations/create-donation/vehicle-${v.vehicle.toLowerCase()}.svg`,
                    isSelected: data.vehicleSize === v.value,
                    imageWidth: '120px',
                    fontSize: '14px',
                    onClick: () => {
                      setValue('vehicleSize', v.value);
                    },
                  }))}
                />
              </Stack>
            </Container>
          ) : step === 8 ? (
            <Container key="step-8">
              <Box textAlign="center">
                <Image src="/static/illustrations/create-donation/instructions.svg" />
                <Typography fontWeight="bold" variant="h3">
                  {t(
                    'donations.where-and-when-should-this-be-collected-and-delivered'
                  )}
                </Typography>
                <Typography mt={2} variant="body2" color="gray">
                  {t('donations.step-9-description')}
                </Typography>
              </Box>

              <Stack mt={2} spacing={2}>
                <Controller
                  control={control}
                  name="pickupInstructions"
                  render={({ field }) => (
                    <TextField
                      size="medium"
                      label={t('donations.pickup-instructions')}
                      fullWidth
                      multiline
                      rows={4}
                      {...field}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="dropOffInstructions"
                  render={({ field }) => (
                    <TextField
                      size="medium"
                      label={t('donations.dropoff-instructions')}
                      fullWidth
                      multiline
                      rows={4}
                      {...field}
                    />
                  )}
                />

                <Controller
                  name="availableOnUnassign"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label={t('donations.notify-if-changes-made-on-edit')}
                    />
                  )}
                />
              </Stack>
            </Container>
          ) : (
            <Container key="step-9">
              <Box textAlign="center">
                <Image src="/static/illustrations/create-donation/recap.svg" />
                <Typography fontWeight="bold" variant="h3">
                  {isFoodRun
                    ? t('donations.great-work-here-is-a-recap', {
                        context: 'food-run',
                      })
                    : t('donations.great-work-here-is-a-recap', {
                        context: 'donation',
                      })}
                </Typography>
                <Typography mt={2} variant="body2" color="gray">
                  {t('donations.recap-description')}
                </Typography>
              </Box>
              <Stack
                sx={{
                  border: (t) => `1px solid ${t.palette.divider}`,
                  borderRadius: '10px',
                  p: 3,
                }}
                mt={2}
              >
                <Grid container spacing={2} rowGap={2}>
                  <Grid item xs={12} md={6}>
                    <EditSection
                      label={t('donations.title')}
                      stepToRedirect={2}
                      setStep={setStep}
                    >
                      <Typography
                        color="primary"
                        variant="h4"
                        fontWeight="bold"
                      >
                        {data.title}
                      </Typography>
                    </EditSection>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <EditSection
                      label={t('donations.description')}
                      stepToRedirect={3}
                      setStep={setStep}
                    >
                      <Typography variant="body1">
                        {data.description}
                      </Typography>
                    </EditSection>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <EditSection
                      label={t('donations.available-dates')}
                      stepToRedirect={5}
                      setStep={setStep}
                    >
                      <Typography variant="body2">
                        {data.startDate &&
                          data.startTime &&
                          `${moment(data.startDate).format(
                            'MMM DD, YYYY'
                          )} - ${moment(data.startTime).format('h:mm A')}`}
                      </Typography>
                      <Typography variant="body2">
                        {data.endDate &&
                          data.endTime &&
                          `${moment(data.endDate).format(
                            'MMM DD, YYYY'
                          )} - ${moment(data.endTime).format('h:mm A')}`}
                      </Typography>
                    </EditSection>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <EditSection
                      label={t('donations.frequency')}
                      stepToRedirect={5}
                      setStep={setStep}
                    >
                      <Typography variant="body2">
                        {data.occurrenceType === 'onetime' &&
                          t('donations.one-time')}
                        {data.occurrenceType === 'recurring' && (
                          <>
                            {t('donations.recurring')}{' '}
                            {data.interval &&
                              `(${t(
                                'donations.' + data.interval.toLowerCase()
                              )})`}
                            {(data.interval === 'Weekly' ||
                              data.interval === 'Bi-weekly') && (
                              <>
                                {' '}
                                {[...'SMTWTFS'].map((_, i) =>
                                  data[`repeat-${i}`] ? (
                                    <Box
                                      key={i}
                                      component="span"
                                      sx={{ fontWeight: 'bold', mx: 0.5 }}
                                    >
                                      [{daysOfTheWeek[i][0]}]
                                    </Box>
                                  ) : null
                                )}
                              </>
                            )}
                            {data.interval === 'Monthly' &&
                              data.dayOfTheMonth && (
                                <>
                                  {data.dayOfTheMonth.isLastDay &&
                                    ` (${t(
                                      'donations.last-day-of-the-month'
                                    )})`}
                                  {data.dayOfTheMonth.customDay &&
                                    ` (${t('donations.day')} ${
                                      data.dayOfTheMonth.customDay
                                    })`}
                                  {data.dayOfTheMonth.week &&
                                    data.dayOfTheMonth.week.number &&
                                    data.dayOfTheMonth.week.dayOfTheWeek !==
                                      null &&
                                    ` (${t(
                                      'donations.' +
                                        ['1st', '2nd', '3rd', '4th', '5th'][
                                          data.dayOfTheMonth.week.number - 1
                                        ]
                                    )} ${
                                      daysOfTheWeek[
                                        data.dayOfTheMonth.week.dayOfTheWeek
                                      ]
                                    })`}
                                </>
                              )}
                          </>
                        )}
                        {data.occurrenceType === 'indefinite' &&
                          t('donations.indefinite')}
                      </Typography>
                    </EditSection>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <EditSection
                      label={t('donations.donation-size')}
                      stepToRedirect={7}
                      setStep={setStep}
                    >
                      <Typography variant="body2">
                        {
                          vehicleTypes.find((v) => v.value === data.vehicleSize)
                            ?.label
                        }
                      </Typography>
                    </EditSection>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <EditSection
                      label={t('donations.photos')}
                      stepToRedirect={0}
                      setStep={setStep}
                    >
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        {data.images.length
                          ? data.images.slice(0, 3).map((image, idx) => {
                              const isUrl = typeof image === 'string';
                              let urlFromFile;
                              if (isUrl) {
                                urlFromFile = getS3Url(image);
                              } else {
                                urlFromFile = URL.createObjectURL(image.file);
                              }
                              return (
                                <Box
                                  key={idx}
                                  component="img"
                                  src={urlFromFile}
                                  alt={`donation-img-${idx}`}
                                  sx={{
                                    width: 60,
                                    height: 60,
                                    objectFit: 'cover',
                                    borderRadius: 1,
                                    border: '1px solid #eee',
                                  }}
                                />
                              );
                            })
                          : t('common.na')}
                      </Box>
                    </EditSection>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <EditSection
                      label={t('donations.food-types')}
                      stepToRedirect={6}
                      setStep={setStep}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 1,
                          mt: 1,
                        }}
                      >
                        {Array.isArray(data.foodTypeNames)
                          ? data.foodTypeNames.map((type, idx) => {
                              const foodTypeObj = foodTypes.find(
                                (f) => f.id === type
                              );
                              const IconComponent = foodTypeObj?.icon;
                              return (
                                <Chip
                                  key={idx}
                                  square
                                  style={{
                                    textTransform: 'capitalize',
                                  }}
                                  label={foodTypeObj.title}
                                  color="primary"
                                  variant="contained"
                                  icon={
                                    IconComponent ? (
                                      <IconComponent
                                        sx={{ width: 20, height: 20 }}
                                      />
                                    ) : null
                                  }
                                />
                              );
                            })
                          : null}
                      </Box>
                    </EditSection>
                  </Grid>
                  {isFoodRun ? (
                    <>
                      <Grid item xs={12} md={6}>
                        <EditSection
                          label={t('donations.dropoff-instructions')}
                          stepToRedirect={8}
                          setStep={setStep}
                        >
                          <Typography variant="body2">
                            {data.dropOffInstructions || t('common.na')}
                          </Typography>
                        </EditSection>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <EditSection
                          label={t('donations.pickup-instructions')}
                          stepToRedirect={8}
                          setStep={setStep}
                        >
                          <Typography variant="body2">
                            {data.pickupInstructions || t('common.na')}
                          </Typography>
                        </EditSection>
                      </Grid>
                    </>
                  ) : null}
                </Grid>
              </Stack>
            </Container>
          )}
        </DialogContent>

        <DialogActions>
          <Container
            sx={{
              ...(step === 6
                ? {
                    maxWidth: '750px',
                  }
                : {}),
            }}
          >
            <Stack direction="row" spacing={2} width="100%">
              <Button
                fullWidth
                size="large"
                variant="outlined"
                color="inherit"
                onClick={() => {
                  clearErrors();
                  if (step === 0) return onClose();
                  if (step === 9 && !isFoodRun) {
                    setStep(7);
                    return;
                  }
                  if (step === 5 && isFoodRun) {
                    setStep(3);
                    return;
                  }

                  setStep((prv) => prv - 1);
                }}
              >
                {step === 0 ? t('cancel') : t('back')}
              </Button>
              <LoadingButton
                onClick={onNextStep}
                loading={loading || loadingImagesAI}
                fullWidth
                size="large"
                variant="contained"
                color="secondary"
              >
                {step === 9
                  ? isFoodRun
                    ? t('donations.create-food-run')
                    : t('donations.create-donation')
                  : t('next')}
              </LoadingButton>
            </Stack>
          </Container>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default DialogCreateDonation;
